export interface DetectionConfig {
  targetWidth: number;
  frameSkip: number;
  blurKernel: number;
  minDist: number;
  param1: number;
  param2: number;
  minRadius: number;
  maxRadius: number;
}

type Preset = "HIGH" | "MEDIUM" | "LOW";

const PRESETS: Record<Preset, DetectionConfig> = {
  HIGH: {
    targetWidth: 640,
    frameSkip: 2,
    blurKernel: 9,
    minDist: 20,
    param1: 50,
    param2: 28,
    minRadius: 5,
    maxRadius: 60,
  },
  MEDIUM: {
    targetWidth: 480,
    frameSkip: 3,
    blurKernel: 7,
    minDist: 25,
    param1: 50,
    param2: 30,
    minRadius: 5,
    maxRadius: 55,
  },
  LOW: {
    targetWidth: 320,
    frameSkip: 4,
    blurKernel: 5,
    minDist: 30,
    param1: 60,
    param2: 35,
    minRadius: 8,
    maxRadius: 50,
  },
};

const DROP_TO_LOW_FPS = 5;
const DROP_TO_MEDIUM_FPS = 9;
const RISE_TO_MEDIUM_FPS = 12;
const RISE_TO_HIGH_FPS = 15;

export class PerformanceManager {
  private frameTimes: number[] = [];
  private preset: Preset = "HIGH";
  private frameCount = 0;

  recordFrameTime(ms: number) {
    this.frameTimes.push(ms);
    if (this.frameTimes.length > 20) this.frameTimes.shift();
  }

  getAverageFps(): number {
    if (this.frameTimes.length < 2) return 10;
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    return Math.round(1000 / avg);
  }

  autoAdjust(): DetectionConfig {
    const fps = this.getAverageFps();
    if (fps < DROP_TO_LOW_FPS) {
      this.preset = "LOW";
    } else if (fps < DROP_TO_MEDIUM_FPS && this.preset === "HIGH") {
      this.preset = "MEDIUM";
    } else if (fps >= RISE_TO_MEDIUM_FPS && this.preset === "LOW") {
      this.preset = "MEDIUM";
    } else if (fps >= RISE_TO_HIGH_FPS && this.preset === "MEDIUM") {
      this.preset = "HIGH";
    }
    return this.getConfig();
  }

  shouldProcessFrame(): boolean {
    this.frameCount += 1;
    return this.frameCount % this.getConfig().frameSkip === 0;
  }

  getConfig(): DetectionConfig {
    return PRESETS[this.preset];
  }

  getPresetName(): Preset {
    return this.preset;
  }
}
